import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, FlaskConical, Map as MapIcon } from 'lucide-react';
import { api } from '../../lib/api';
import { Button, Card, Field, Input, Pill } from '../../components/ui';

/**
 * Which service answers map search, and whether Google basemaps are offered
 * beside OpenStreetMap and Esri satellite.
 *
 * Google is optional and stays off until somebody saves a key: the platform
 * ships with OSM street tiles, Esri satellite tiles and Nominatim search, none
 * of which need one. This card exists to add Google beside them and — because
 * the operator asked for it in as many words — to say exactly which Google
 * Cloud APIs have to be switched on, where the key goes, and what to restrict
 * it to, written for somebody opening that console for the first time.
 *
 * The key itself is NOT on this card. It lives on the API keys page with every
 * other secret the platform holds, the same split the file-storage card uses.
 */
export interface GoogleApiDoc {
  id: string;
  name: string;
  service: string;
  console: string;
  neededFor: string;
}

export interface MapProviderView {
  geocoder: 'nominatim' | 'google';
  tiles: boolean;
  region: string;
  language: string;
  keyConfigured: boolean;
  keyHint: string;
  effectiveGeocoder: 'nominatim' | 'google';
  lastFailure: {
    reason: string;
    message: string;
    surface: 'geocode' | 'tiles';
    at: number;
  } | null;
  googleApis: GoogleApiDoc[];
  mapTypes: string[];
}

interface TestResult {
  geocode: { ok: boolean; reason?: string; message: string };
  tiles: { ok: boolean; reason?: string; message: string } | null;
}

export function MapProviderCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['map-provider', 'settings'],
    queryFn: () => api.get<MapProviderView>('/map-provider/settings'),
  });

  const [geocoder, setGeocoder] = useState<'nominatim' | 'google' | null>(null);
  const [tiles, setTiles] = useState<boolean | null>(null);
  const [form, setForm] = useState<{ region: string; language: string }>({ region: '', language: '' });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<TestResult | null>(null);

  const seeded = useRef(false);
  useEffect(() => {
    if (!query.data || seeded.current) return;
    seeded.current = true;
    setForm({ region: query.data.region, language: query.data.language });
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/map-provider/settings', {
        geocoder: geocoder ?? undefined,
        tiles: tiles ?? undefined,
        region: form.region || undefined,
        language: form.language || undefined,
      }),
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['map-provider'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const test = useMutation({
    mutationFn: () => api.post<TestResult>('/map-provider/test'),
    onSuccess: async (r) => {
      setTested(r);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['map-provider'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Test failed'),
  });

  const s = query.data;
  if (!s) return null;
  const activeGeocoder = geocoder ?? s.geocoder;
  const activeTiles = tiles ?? s.tiles;
  const geocodeApi = s.googleApis.find((a) => a.id === 'geocoding');
  const tilesApi = s.googleApis.find((a) => a.id === 'tiles');

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <MapIcon className="size-4 text-muted" />
          Map provider
        </span>
      }
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={<FlaskConical className="size-3.5" />}
          loading={test.isPending}
          disabled={!s.keyConfigured}
          onClick={() => test.mutate()}
        >
          Test the key
        </Button>
      }
    >
      <div className="space-y-5 p-5">
        <p className="text-sm text-muted">
          The topology map draws OpenStreetMap street tiles and Esri satellite tiles, and searches
          place names through OpenStreetMap. None of that needs a key and none of it changes here.
          Google is an extra option: switch it on once a key is saved, and switch it off again to go
          straight back to what you have today.
        </p>

        {/* --- What answers map search ------------------------------------ */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-ink">Map search</p>
          <div className="flex flex-wrap gap-2">
            {(['nominatim', 'google'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGeocoder(g)}
                className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                  activeGeocoder === g
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-line text-muted hover:text-ink'
                }`}
              >
                {g === 'nominatim' ? 'OpenStreetMap (no key)' : 'Google Geocoding'}
                {g === 'google' && s.keyConfigured && ' ✓'}
              </button>
            ))}
          </div>
          {activeGeocoder === 'google' && !s.keyConfigured && (
            <p className="text-xs text-warn">
              No key is saved yet — searches will keep coming from OpenStreetMap until one is.
            </p>
          )}
          {s.geocoder === 'google' && s.effectiveGeocoder === 'nominatim' && (
            <p className="text-xs text-warn">
              Google is selected but OpenStreetMap is answering.
              {s.lastFailure ? ` ${s.lastFailure.message}` : ''}
            </p>
          )}
        </div>

        {/* --- Basemaps ---------------------------------------------------- */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-ink">Basemaps</p>
          <p className="text-xs text-faint">
            OpenStreetMap streets and Esri satellite are always in the layer picker. Google adds{' '}
            {s.mapTypes.join(', ')} beside them.
          </p>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-primary"
              checked={activeTiles}
              onChange={(e) => setTiles(e.target.checked)}
            />
            <span>
              Offer Google basemaps on the topology map
              <span className="mt-1 block text-xs text-faint">
                Tiles are fetched by this server and passed on, so the key is never in a URL your
                browser can see. The cost of that: every tile travels twice — Google to this server,
                this server to the browser — and each one is still a billed Map Tiles API request.
              </span>
            </span>
          </label>
        </div>

        {/* --- Regional settings ------------------------------------------- */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Region (two-letter code — biases search results, e.g. KE)">
            <Input
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              className="font-mono"
              placeholder="KE"
            />
          </Field>
          <Field label="Language (tag for tile labels, e.g. en-GB)">
            <Input
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              className="font-mono"
              placeholder="en-GB"
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Pill state tone={s.keyConfigured ? 'success' : 'neutral'}>
            {s.keyConfigured ? `key saved · ${s.keyHint}` : 'no key saved'}
          </Pill>
          <Pill tone={s.effectiveGeocoder === 'google' ? 'info' : 'neutral'}>
            search: {s.effectiveGeocoder === 'google' ? 'Google' : 'OpenStreetMap'}
          </Pill>
          <span className="text-xs text-faint">
            The key is entered on the{' '}
            <Link className="text-primary hover:underline" to="/settings/api-keys">
              API keys page
            </Link>
            , with every other secret the platform holds.
          </span>
        </div>

        {tested && (
          <div className="space-y-1 rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm">
            <p className={tested.geocode.ok ? 'text-success' : 'text-danger'}>
              Geocoding API: {tested.geocode.message}
            </p>
            {tested.tiles && (
              <p className={tested.tiles.ok ? 'text-success' : 'text-danger'}>
                Map Tiles API: {tested.tiles.message}
              </p>
            )}
            {!tested.tiles && (
              <p className="text-xs text-faint">
                Google basemaps are off, so the Map Tiles API was not called.
              </p>
            )}
          </div>
        )}

        {error && <p className="text-sm font-medium text-danger">{error}</p>}
        {notice && <p className="text-sm text-success">{notice}</p>}

        {/* --- The console instructions ------------------------------------ */}
        <details className="rounded-lg border border-line bg-surface-2 px-4 py-3" open={!s.keyConfigured}>
          <summary className="cursor-pointer text-sm font-semibold text-ink">
            Setting Google up in the Google Cloud console
          </summary>
          <ol className="mt-3 list-decimal space-y-3 pl-5 text-sm text-muted">
            <li>
              At <span className="font-mono text-xs">console.cloud.google.com</span>, pick a project
              or create one, and attach a billing account to it. Google Maps Platform refuses every
              request on a project with no billing account, including the ones inside the free
              monthly credit — a project without one answers{' '}
              <span className="font-mono text-xs">REQUEST_DENIED</span> to everything.
            </li>
            <li>
              Turn on the APIs for the features you are actually using. Each link below opens that
              API's page in the console; press <em>Enable</em>.
              <ul className="mt-2 space-y-2">
                {geocodeApi && (
                  <li className="rounded-md border border-line bg-surface px-3 py-2">
                    <a
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                      href={geocodeApi.console}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {geocodeApi.name}
                      <ExternalLink className="size-3" />
                    </a>
                    <span className="ml-2 font-mono text-[11px] text-faint">
                      {geocodeApi.service}
                    </span>
                    <p className="text-xs text-faint">
                      {geocodeApi.neededFor} Required whenever the search above is set to Google.
                    </p>
                  </li>
                )}
                {tilesApi && (
                  <li className="rounded-md border border-line bg-surface px-3 py-2">
                    <a
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                      href={tilesApi.console}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {tilesApi.name}
                      <ExternalLink className="size-3" />
                    </a>
                    <span className="ml-2 font-mono text-[11px] text-faint">{tilesApi.service}</span>
                    <p className="text-xs text-faint">
                      {tilesApi.neededFor} Only needed if you tick the basemaps box above — leave it
                      off and this API can stay disabled.
                    </p>
                  </li>
                )}
              </ul>
              <p className="mt-2 text-xs text-faint">
                Nothing else needs enabling. In particular the Maps JavaScript API and the Places API
                are not used by this platform, so leaving them off costs you nothing and is one less
                surface on the key.
              </p>
            </li>
            <li>
              Make the key: <em>APIs &amp; Services → Credentials → Create credentials → API key</em>.
              Copy it while the dialog is open.
            </li>
            <li>
              Restrict it before you use it, in <em>Edit API key</em>:
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                <li>
                  <strong>Application restrictions → IP addresses.</strong> Add this server's public
                  IP address (and its IPv6 address if it has one). Both APIs are called from this
                  server and never from a browser, so an IP restriction is the tight one — a leaked
                  key is then useless from anywhere else. Do <em>not</em> choose Websites/HTTP
                  referrers: nothing here calls Google from the browser.
                </li>
                <li>
                  <strong>API restrictions → Restrict key.</strong> Select only{' '}
                  {geocodeApi?.name ?? 'Geocoding API'}
                  {activeTiles ? ` and ${tilesApi?.name ?? 'Map Tiles API'}` : ''}. A key that can do
                  one thing can only ever be billed for that thing.
                </li>
              </ul>
            </li>
            <li>
              Paste the key on the{' '}
              <Link className="text-primary hover:underline" to="/settings/api-keys">
                API keys page
              </Link>{' '}
              under <em>Map provider</em>. It is stored encrypted, it is never sent to a browser, and
              it never appears in a log or an API response.
            </li>
            <li>
              Come back here, press <em>Test the key</em>, then switch search and basemaps over. If
              Google ever refuses the key or runs out of quota, search falls back to OpenStreetMap by
              itself and says so — you do not have to be watching.
            </li>
          </ol>
        </details>
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-line px-5 py-3.5">
        <span className="text-xs text-faint">
          OpenStreetMap and Esri keep working whatever is chosen here.
        </span>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </footer>
    </Card>
  );
}
